<template>
  <el-dialog
    title="BULK CREATE/UPDATE PRODUCT ATTRIBUTES"
    :visible.sync="dialogIsOpen"
    :fullscreen="true"
    :close-on-click-modal="false"
    append-to-body
    class="product-attribute-bulk-dialog"
    @closed="closeDialog"
  >
    <div class="product-attribute-bulk-form">
      <div ref="spreadsheet" class="product-attribute-bulk-spreadsheet"></div>
      <div class="d-flex justify-content-start mt-3">
        <mdb-btn v-if="dependenciesLoading || loading" color="elegant" size="sm" disabled>
          <span class="spinner-border spinner-border-sm mr-1"></span>
          Loading...
        </mdb-btn>
        <mdb-btn v-else color="elegant" size="sm" :disabled="!spreadsheet" @click="submit">SAVE</mdb-btn>
        <mdb-btn color="grey" size="sm" :disabled="loading" @click="dialogIsOpen = false">CLOSE</mdb-btn>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import { mdbBtn } from 'mdbvue'
import { Message, MessageBox } from 'element-ui'
import { mapActions } from 'vuex'
import jspreadsheet from '../../jsexcel/jexcel'
import { ApiHttpService } from '../../services'

export default {
  components: { mdbBtn },
  data() {
    return {
      dialogIsOpen: false,
      loading: false,
      dependenciesLoading: false,
      spreadsheet: null,
      displayTypes: [
        { id: 'TEXT', name: 'TEXT', active: false },
        { id: 'NUMBER', name: 'NUMBER', active: false },
        { id: 'BOOLEAN', name: 'Yes/No', active: true },
        { id: 'LIST', name: 'List', active: true },
      ],
      statusOptions: [
        { id: '1', name: 'Active' },
        { id: '0', name: 'Inactive' },
      ],
    }
  },
  computed: {
    activeDisplayTypes() {
      return this.displayTypes.filter(type => type.active)
    },
  },
  methods: {
    ...mapActions('ProductAttributeModule', ['bulkStore']),
    async openDialog() {
      this.dialogIsOpen = true
      this.dependenciesLoading = true
      try {
        const response = await ApiHttpService.getRequest('product-attributes', { length: 1000, with_options: 1 })
        const attributes = response.data && Array.isArray(response.data.data)
          ? response.data.data
          : (Array.isArray(response.data) ? response.data : [])
        await this.$nextTick()
        if (this.dialogIsOpen) this.renderTable(attributes)
      } catch (error) {
        Message.error('Unable to load Product Attributes.')
      } finally {
        this.dependenciesLoading = false
      }
    },
    closeDialog() {
      this.dialogIsOpen = false
      if (this.spreadsheet) {
        this.spreadsheet.destroy()
        this.spreadsheet = null
      }
    },
    renderTable(attributes = []) {
      if (!this.$refs.spreadsheet) return
      if (this.spreadsheet) this.spreadsheet.destroy()
      const rows = attributes.map(attribute => [
        attribute.id || '',
        attribute.name || '',
        String(attribute.data_type || '').toUpperCase(),
        attribute.active === undefined ? '' : (['1', 1, true, 'true'].includes(attribute.active) ? '1' : '0'),
        this.getOptionsValue(attribute.options),
        '',
      ])
      while (rows.length < 50) rows.push(['', '', '', '', '', ''])
      this.spreadsheet = jspreadsheet(this.$refs.spreadsheet, {
        data: rows,
        minDimensions: [6, 50],
        allowInsertRow: true,
        allowDeleteRow: true,
        allowInsertColumn: false,
        allowDeleteColumn: false,
        allowRenameColumn: false,
        tableWidth: '100%',
        tableOverflow: true,
        tableHeight: 'calc(100vh - 105px)',
        columns: [
          { type: 'hidden', title: 'ID', name: 'id', width: 1, readOnly: true },
          { type: 'text', title: 'NAME', name: 'name', width: 280 },
          { type: 'dropdown', title: 'DISPLAY TYPE', name: 'data_type', width: 180, autocomplete: true, source: this.activeDisplayTypes },
          { type: 'dropdown', title: 'STATUS', name: 'active', width: 140, source: this.statusOptions },
          { type: 'text', title: 'OPTIONS', name: 'options', width: 360 },
          { type: 'text', title: 'RESPONSE', name: 'response', width: 420, readOnly: true },
        ],
        onchange: (instance, cell, column, row, value) => {
          const grid = instance.jexcel || instance
          if (Number(column) !== 2) return
          if (String(value).toUpperCase() !== 'LIST') {
            grid.setValueFromCoords(4, row, '', true)
          }
        },
      })
      if (this.spreadsheet.setWidth) this.spreadsheet.setWidth(window.innerWidth)
    },
    getOptionsValue(options) {
      if (Array.isArray(options)) {
        return options
          .map(option => typeof option === 'object' ? (option.value || option.name || option.text || '') : option)
          .filter(Boolean)
          .join('|')
      }
      return options || ''
    },
    getRows() {
      return this.spreadsheet.getData().map((row, index) => ({
        id: row[0] ? String(row[0]).trim() : '',
        name: row[1] ? String(row[1]).trim() : '',
        data_type: String(row[2] || '').trim().toUpperCase(),
        active: row[3] === '' ? '' : ['1', 1, true, 'true'].includes(row[3]),
        options: row[4] ? String(row[4]) : '',
        response: row[5] || '',
        row: index + 1,
      })).filter(row => row.id || row.name || row.data_type || row.active !== '' || row.options)
    },
    setResponse(message, rowNumber) {
      if (this.spreadsheet && rowNumber) {
        this.spreadsheet.setValueFromCoords(5, rowNumber - 1, message, true)
      }
    },
    setRowStatus(rowNumber, status) {
      if (!this.spreadsheet || !rowNumber) return
      const backgroundColor = status === 'success' ? '#04b65d' : 'pink'
      const columns = ['A', 'B', 'C', 'D', 'E', 'F']
      columns.forEach(column => {
        this.spreadsheet.setStyle(column + rowNumber, 'background-color', backgroundColor, true)
      })
    },
    applyResults(rows, response) {
      const results = Array.isArray(response && response.rows) ? response.rows : []
      const resultsByRow = {}
      results.forEach(result => {
        const responseRow = Number(result.row)
        if (Number.isInteger(responseRow)) resultsByRow[responseRow] = result
      })
      rows.forEach(row => {
        const result = resultsByRow[row.row]
        const isMissing = !result
        const message = isMissing
          ? 'No API result was returned for this row.'
          : (result.message || (response && response.message) || 'Saved successfully.')
        const isError = isMissing || Number(result.error) !== 0
        this.setResponse(message, row.row)
        this.setRowStatus(row.row, isError ? 'error' : 'success')
        if (!isError && result.id) {
          this.spreadsheet.setValueFromCoords(0, row.row - 1, result.id, true)
        }
      })
      return rows.some(row => !resultsByRow[row.row]) || results.some(result => Number(result.error) !== 0)
    },
    async submit() {
      if (!this.spreadsheet) {
        Message.warning('Product Attributes are still loading. Please retry before saving.')
        return
      }
      const rows = this.getRows()
      if (!rows.length) {
        Message.warning('Add at least one Product Attribute row.')
        return
      }
      await MessageBox.confirm('Save ' + rows.length + ' Product Attribute' + (rows.length === 1 ? '' : 's') + '?', 'Confirm bulk save', { type: 'warning' })
      this.loading = true
      try {
        const payload = rows.map(({ response, ...item }) => item)
        const response = await this.bulkStore(payload)
        const hasErrors = this.applyResults(rows, response)
        if (hasErrors) {
          Message.error(response.message || 'Some Product Attribute rows could not be saved.')
        } else {
          Message.success(response.message || 'Product Attributes saved successfully.')
        }
        this.$emit('success', response)
      } catch (error) {
        const responseData = error.response && error.response.data ? error.response.data : {}
        const responseRows = Array.isArray(responseData.rows) ? responseData : null
        if (responseRows) {
          this.applyResults(rows, responseRows)
        }
        const message = responseData.message || 'Product Attribute bulk save failed.'
        if (!responseRows) {
          rows.forEach(row => {
            this.setResponse(message, row.row)
            this.setRowStatus(row.row, 'error')
          })
        }
        Message.error(message)
      } finally {
        this.loading = false
      }
    },
  },
  beforeDestroy() {
    if (this.spreadsheet) this.spreadsheet.destroy()
    this.spreadsheet = null
  },
}
</script>

<style scoped>
.product-attribute-bulk-form {
  min-height: calc(100vh - 80px);
}
.product-attribute-bulk-spreadsheet {
  width: 100%;
}
.product-attribute-bulk-dialog /deep/ .el-dialog__body {
  padding: 0;
}
</style>
